import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  Calendar,
  Car,
  Clock,
  ClipboardList,
  Minus,
  Plus,
  ShieldCheck,
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Modal from "./Modal";
import { useToast } from "../hooks/useToast";
import { ADDON_UNIT_HOURS, computeAddOnsTotal } from "../data/bookingAddOns";
import {
  BLOCK_HOURS,
  DEFAULT_RETURN_BUFFER_MINUTES,
  MIN_LEAD_HOURS,
  addBlocks,
  earliestBookableStart,
  getAvailableStartTimes,
  getBlockDiscountRate,
  getFullyBookedDays,
  isStartAvailable,
  parseBusyWindows,
} from "../utils/bookingBlocks";

const formatMoney = (amount, symbol = "₦") => {
  const numeric = Number(amount) || 0;
  return `${symbol}${numeric.toLocaleString("en-NG")}`;
};

const datePickerLayerProps = {
  calendarClassName: "pulo-datepicker",
  popperClassName: "pulo-datepicker-popper",
};

const formatDateTime = (value) =>
  value
    ? value.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    : "—";

// How far ahead the calendar offers dates. Availability is fetched for the
// same window, so anything the user can click has been checked.
const BOOKING_HORIZON_DAYS = 180;

function Stepper({ value, onDecrease, onIncrease, label, suffix }) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onDecrease}
        className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:border-[#570033] hover:text-[#570033]"
        aria-label={`Decrease ${label}`}
      >
        <Minus className="h-3 w-3" />
      </button>
      <span className="min-w-[40px] text-center text-sm font-semibold text-slate-700">
        {value}
        {suffix || ""}
      </span>
      <button
        type="button"
        onClick={onIncrease}
        className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:border-[#570033] hover:text-[#570033]"
        aria-label={`Increase ${label}`}
      >
        <Plus className="h-3 w-3" />
      </button>
    </div>
  );
}

function AddOnRow({
  option,
  selection,
  onQtyChange,
  onHoursChange,
  currencySymbol,
}) {
  const { key, label, price, unitHours = ADDON_UNIT_HOURS } = option;
  const quantity = selection?.quantity || 0;
  const hours = selection?.hours || unitHours;

  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-slate-800">{label}</p>
        <p className="text-xs font-medium text-[#570033]">
          {formatMoney(price, currencySymbol)} / {unitHours}hrs
        </p>
      </div>
      <div className="flex items-center gap-4">
        <Stepper
          label={`${label} quantity`}
          value={quantity}
          onDecrease={() => onQtyChange(key, Math.max(0, quantity - 1))}
          onIncrease={() => onQtyChange(key, quantity + 1)}
        />
        <Stepper
          label={`${label} duration`}
          value={hours}
          suffix="hrs"
          onDecrease={() =>
            onHoursChange(key, Math.max(unitHours, hours - unitHours))
          }
          onIncrease={() => onHoursChange(key, hours + unitHours)}
        />
      </div>
    </div>
  );
}

function AssistantBookingModal({
  isOpen,
  onClose,
  onProceed,
  escortVehicles = [],
  securityPersonnel = [],
  currencySymbol = "₦",
  assistantFirstName = "",
  pricePerBlock = 0,
  busy = [],
  returnBufferMinutes = DEFAULT_RETURN_BUFFER_MINUTES,
  isAvailabilityLoading = false,
  availabilityError = "",
}) {
  // The assistant's time is sold in whole 12-hour blocks, so the user picks a
  // start and a block count; departure is derived rather than chosen.
  const [arrivalDate, setArrivalDate] = useState(null);
  const [arrivalTime, setArrivalTime] = useState(null);
  const [blocks, setBlocks] = useState(1);
  const [tourists, setTourists] = useState(1);
  const [itinerary, setItinerary] = useState("");
  // Keyed selections: { [optionKey]: { quantity, hours } }
  const [vehicleSelections, setVehicleSelections] = useState({});
  const [securitySelections, setSecuritySelections] = useState({});
  const toast = useToast();

  // Trips must be booked at least 12 hours out, so the calendar starts there
  // rather than at "now". Every slot the modal offers is filtered against this
  // instant, and the API rejects anything sooner regardless.
  const earliestStart = useMemo(
    () => earliestBookableStart(),
    // Recomputed when the modal is reopened; a modal left open all day is not
    // worth a ticking clock.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen]
  );

  const firstBookableDay = useMemo(() => {
    const day = new Date(earliestStart);
    day.setHours(0, 0, 0, 0);
    return day;
  }, [earliestStart]);

  const maxDate = useMemo(() => {
    const limit = new Date(firstBookableDay);
    limit.setDate(limit.getDate() + BOOKING_HORIZON_DAYS);
    return limit;
  }, [firstBookableDay]);

  const busyWindows = useMemo(() => parseBusyWindows(busy), [busy]);

  // Reset selections when reopened so stale picks don't linger.
  useEffect(() => {
    if (!isOpen) return;
    setVehicleSelections({});
    setSecuritySelections({});
    setItinerary("");
    setArrivalDate(null);
    setArrivalTime(null);
    setBlocks(1);
  }, [isOpen]);

  // Start times still free on the chosen day, at this length. Recomputed when
  // the block count changes — a longer booking fits in fewer places.
  const availableStartTimes = useMemo(
    () =>
      getAvailableStartTimes({
        date: arrivalDate,
        blocks,
        busyWindows,
        returnBufferMinutes,
        now: earliestStart,
      }),
    [arrivalDate, blocks, busyWindows, returnBufferMinutes, earliestStart]
  );

  const fullyBookedDays = useMemo(
    () =>
      getFullyBookedDays({
        from: firstBookableDay,
        to: maxDate,
        blocks,
        busyWindows,
        returnBufferMinutes,
        now: earliestStart,
      }),
    [firstBookableDay, maxDate, blocks, busyWindows, returnBufferMinutes, earliestStart]
  );

  // Growing the booking can swallow the slot already chosen; drop it rather
  // than carry a start the assistant can no longer honour.
  useEffect(() => {
    if (!arrivalTime) return;
    if (
      !isStartAvailable({
        start: arrivalTime,
        blocks,
        busyWindows,
        returnBufferMinutes,
        now: earliestStart,
      })
    ) {
      setArrivalTime(null);
    }
  }, [arrivalTime, blocks, busyWindows, returnBufferMinutes, earliestStart]);

  const departure = useMemo(
    () => (arrivalTime ? addBlocks(arrivalTime, blocks) : null),
    [arrivalTime, blocks]
  );

  const adjustTourists = (delta) =>
    setTourists((prev) => Math.max(1, prev + delta));

  const updateSelection = (setter, options) => (key, field, value) => {
    setter((prev) => {
      const option = options.find((o) => o.key === key);
      const unitHours = option?.unitHours || ADDON_UNIT_HOURS;
      const current = prev[key] || { quantity: 0, hours: unitHours };
      return { ...prev, [key]: { ...current, [field]: value } };
    });
  };

  const setVehicleQty = (key, value) =>
    updateSelection(setVehicleSelections, escortVehicles)(key, "quantity", value);
  const setVehicleHours = (key, value) =>
    updateSelection(setVehicleSelections, escortVehicles)(key, "hours", value);
  const setSecurityQty = (key, value) =>
    updateSelection(setSecuritySelections, securityPersonnel)(key, "quantity", value);
  const setSecurityHours = (key, value) =>
    updateSelection(setSecuritySelections, securityPersonnel)(key, "hours", value);

  const buildSelectionList = (options, selections) =>
    options
      .map((option) => {
        const unit = option.unitHours || ADDON_UNIT_HOURS;
        const sel = selections[option.key] || {};
        const quantity = sel.quantity || 0;
        const hours = sel.hours || unit;
        return {
          key: option.key,
          label: option.label,
          price: option.price,
          unitHours: unit,
          quantity,
          hours,
          subtotal: quantity > 0 ? option.price * quantity * (hours / unit) : 0,
        };
      })
      .filter((item) => item.quantity > 0);

  const vehicleList = useMemo(
    () => buildSelectionList(escortVehicles, vehicleSelections),
    [escortVehicles, vehicleSelections]
  );
  const securityList = useMemo(
    () => buildSelectionList(securityPersonnel, securitySelections),
    [securityPersonnel, securitySelections]
  );

  const addOnsTotal = useMemo(
    () => computeAddOnsTotal([...vehicleList, ...securityList]),
    [vehicleList, securityList]
  );

  // Priced the same way the API prices it — rate per block × blocks, with the
  // volume discount — so the figure here matches what Paystack is asked for.
  const discountRate = useMemo(() => getBlockDiscountRate(blocks), [blocks]);
  const discountPercent = useMemo(
    () => Math.round((1 - discountRate) * 100),
    [discountRate]
  );
  const assistantFee = useMemo(
    () => Number(((Number(pricePerBlock) || 0) * blocks * discountRate).toFixed(2)),
    [pricePerBlock, blocks, discountRate]
  );
  const bookingTotal = useMemo(
    () => Number((assistantFee + addOnsTotal).toFixed(2)),
    [assistantFee, addOnsTotal]
  );

  const handleProceed = () => {
    if (!arrivalDate) {
      toast.error("Please select your arrival date.");
      return;
    }
    if (!arrivalTime) {
      toast.error("Please select a start time.");
      return;
    }
    // Availability may have moved while the modal sat open. The API re-checks
    // and answers 409, but catching it here saves a round trip.
    if (
      !isStartAvailable({
        start: arrivalTime,
        blocks,
        busyWindows,
        returnBufferMinutes,
      })
    ) {
      toast.error("That time is no longer available. Please pick another.");
      setArrivalTime(null);
      return;
    }
    if (onProceed) {
      onProceed({
        arrivalDate: arrivalTime.toISOString(),
        departureDate: departure.toISOString(),
        blocks,
        tourists,
        itinerary: itinerary.trim(),
        escortVehicles: vehicleList,
        securityPersonnel: securityList,
        addOnsTotal: Number(addOnsTotal.toFixed(2)),
      });
    }
  };

  const hasVehicles = escortVehicles.length > 0;
  const hasSecurity = securityPersonnel.length > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} panelClassName="max-w-lg">
      <div className="max-h-[80vh] space-y-6 overflow-y-auto pr-1">
        <div className="space-y-2 pr-10">
          <h3 className="text-lg font-bold text-[#570033]">Pulo Assistant</h3>
          <p className="text-sm text-slate-500">
            To secure your booking, please provide the following information.
          </p>
        </div>

        {availabilityError ? (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{availabilityError}</span>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="space-y-2 text-xs font-semibold text-slate-600">
            Arrival date
            <div className="relative">
              <Calendar className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <DatePicker
                {...datePickerLayerProps}
                selected={arrivalDate}
                onChange={(date) => {
                  setArrivalDate(date);
                  setArrivalTime(null);
                }}
                minDate={firstBookableDay}
                maxDate={maxDate}
                excludeDates={fullyBookedDays}
                dayClassName={(date) =>
                  fullyBookedDays.some(
                    (booked) => booked.toDateString() === date.toDateString()
                  )
                    ? "pulo-day-booked"
                    : undefined
                }
                showPopperArrow={false}
                shouldCloseOnSelect={true}
                onFocus={(e) => e.target.blur()}
                popperPlacement="bottom-start"
                className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm text-slate-700 outline-none focus:border-[#570033]"
                placeholderText={
                  isAvailabilityLoading ? "Loading availability..." : "Arrival date"
                }
                dateFormat="MMM d, yyyy"
                disabled={isAvailabilityLoading}
              />
            </div>
          </label>

          <label className="space-y-2 text-xs font-semibold text-slate-600">
            Start time
            <div className="relative">
              <Clock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <DatePicker
                {...datePickerLayerProps}
                selected={arrivalTime}
                onChange={(time) => setArrivalTime(time)}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={30}
                timeCaption="Time"
                dateFormat="h:mm aa"
                // Only starts that leave room for the whole booking. An empty
                // list means the day filled up while the modal was open.
                includeTimes={availableStartTimes}
                openToDate={arrivalDate || undefined}
                showPopperArrow={false}
                shouldCloseOnSelect={true}
                onFocus={(e) => e.target.blur()}
                popperPlacement="bottom-start"
                className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm text-slate-700 outline-none focus:border-[#570033] disabled:cursor-not-allowed disabled:bg-slate-50"
                placeholderText={
                  !arrivalDate
                    ? "Pick a date first"
                    : availableStartTimes.length
                    ? "Start time"
                    : "No times left"
                }
                disabled={!arrivalDate || !availableStartTimes.length}
              />
            </div>
          </label>
        </div>

        <p className="text-xs text-slate-500">
          Trips start at least {MIN_LEAD_HOURS} hours from now — your assistant
          needs time to prepare.
        </p>

        <div className="space-y-3 rounded-lg border border-slate-200 px-3 py-3">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-semibold text-slate-700">
                How long do you need {assistantFirstName || "your assistant"}?
              </span>
              <p className="text-xs text-slate-500">
                Booked in {BLOCK_HOURS}-hour blocks
              </p>
            </div>
            <Stepper
              label="blocks"
              value={blocks}
              suffix={` × ${BLOCK_HOURS}hrs`}
              onDecrease={() => setBlocks((prev) => Math.max(1, prev - 1))}
              onIncrease={() => setBlocks((prev) => prev + 1)}
            />
          </div>

          <div className="flex items-center justify-between border-t border-slate-100 pt-3 text-xs">
            <span className="font-semibold text-slate-500">Ends</span>
            <span className="font-semibold text-slate-700">
              {formatDateTime(departure)}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-3">
          <span className="text-sm font-semibold text-slate-700">
            How many tourists are we expecting?
          </span>
          <Stepper
            label="tourists"
            value={tourists}
            onDecrease={() => adjustTourists(-1)}
            onIncrease={() => adjustTourists(1)}
          />
        </div>

        <label className="block space-y-2 text-xs font-semibold text-slate-600">
          Itinerary
          <div className="relative">
            <ClipboardList className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <textarea
              value={itinerary}
              onChange={(event) => setItinerary(event.target.value)}
              maxLength={2000}
              rows={4}
              className="min-h-[112px] w-full resize-y rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-[#570033]"
              placeholder="Share your schedule, stops, pickup notes, or anything your assistant should know."
            />
          </div>
        </label>

        {hasVehicles ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm font-bold text-[#570033]">
              <Car className="h-4 w-4" />
              Escort Vehicle
            </div>
            <p className="text-xs text-slate-500">
              Select convoy vehicles (priced per {ADDON_UNIT_HOURS}hrs)
            </p>
            <div className="divide-y divide-slate-100">
              {escortVehicles.map((option) => (
                <AddOnRow
                  key={option.key}
                  option={option}
                  selection={vehicleSelections[option.key]}
                  onQtyChange={setVehicleQty}
                  onHoursChange={setVehicleHours}
                  currencySymbol={currencySymbol}
                />
              ))}
            </div>
          </div>
        ) : null}

        {hasSecurity ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm font-bold text-[#570033]">
              <ShieldCheck className="h-4 w-4" />
              Security Personnel
            </div>
            <p className="text-xs text-slate-500">
              Select security personnel (priced per {ADDON_UNIT_HOURS}hrs)
            </p>
            <div className="divide-y divide-slate-100">
              {securityPersonnel.map((option) => (
                <AddOnRow
                  key={option.key}
                  option={option}
                  selection={securitySelections[option.key]}
                  onQtyChange={setSecurityQty}
                  onHoursChange={setSecurityHours}
                  currencySymbol={currencySymbol}
                />
              ))}
            </div>
          </div>
        ) : null}

        <div className="space-y-2 rounded-lg bg-slate-100 px-3 py-3 text-sm text-[#570033]">
          <div className="flex items-center justify-between">
            <span>
              Assistant · {blocks} × {BLOCK_HOURS}hrs
            </span>
            <span className="font-semibold">
              {formatMoney(assistantFee, currencySymbol)}
            </span>
          </div>
          {discountPercent > 0 ? (
            <div className="flex items-center justify-between text-xs text-emerald-700">
              <span>Longer-booking discount</span>
              <span>-{discountPercent}%</span>
            </div>
          ) : null}
          {addOnsTotal > 0 ? (
            <div className="flex items-center justify-between">
              <span>Add-ons</span>
              <span className="font-semibold">
                {formatMoney(addOnsTotal, currencySymbol)}
              </span>
            </div>
          ) : null}
          <div className="flex items-center justify-between border-t border-slate-200 pt-2 text-base font-bold">
            <span>Total</span>
            <span>{formatMoney(bookingTotal, currencySymbol)}</span>
          </div>
        </div>

        <div className="flex items-start gap-2 rounded-xl bg-[#FFF5E6] px-3 py-3 text-xs font-medium text-[#7A4C00]">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Add any schedule details your Pulo Assistant should review before
            accepting the booking.
          </p>
        </div>

        <button
          type="button"
          onClick={handleProceed}
          className="w-full rounded-xl bg-[#570033] py-3 text-sm font-semibold text-white transition hover:bg-[#450029]"
        >
          Proceed
        </button>
      </div>
    </Modal>
  );
}

export default AssistantBookingModal;
